import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Form, Spinner } from 'react-bootstrap';
import programService from '@/shared/services/programService';

/**
 * The web page a student can read to learn about the program. Other
 * programs' application questions link to it ("Besides being an LA, would
 * you also be interested in..."). Empty means no link is shown.
 */
function ProgramPageLinkCard({ programType, programLabel }) {
    const [url, setUrl] = useState('');
    const [saved, setSaved] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [loadError, setLoadError] = useState(null);
    const [saveError, setSaveError] = useState(null);
    const [notice, setNotice] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        setLoadError(null);
        try {
            const config = await programService
                .getConfig(programType)
                .catch((err) => {
                    if (err.response?.status === 404) return {};
                    throw err;
                });
            setSaved(config.pageUrl || '');
            setUrl(config.pageUrl || '');
        } catch (_err) {
            setLoadError('Failed to load the program page link.');
        } finally {
            setLoading(false);
        }
    }, [programType]);

    useEffect(() => {
        load();
    }, [load]);

    const inputId = `program-page-url-${programType}`;
    const unchanged = url.trim() === saved;

    const save = async (event) => {
        event.preventDefault();
        if (saving) return;
        setSaving(true);
        setSaveError(null);
        setNotice('');
        try {
            const config = await programService.upsertConfig(programType, {
                pageUrl: url.trim() === '' ? null : url.trim(),
            });
            const stored = config?.pageUrl || '';
            setSaved(stored);
            setUrl(stored);
            setNotice(
                stored
                    ? `Students are now pointed to ${stored}.`
                    : 'The program page link is cleared.'
            );
        } catch (err) {
            setSaveError(
                err.response?.data?.message ||
                    'Failed to save the program page link.'
            );
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="text-center py-4" role="status">
                <Spinner animation="border" size="sm" className="me-2" />
                Loading the program page link...
            </div>
        );
    }
    if (loadError) return <Alert variant="danger">{loadError}</Alert>;

    return (
        <Form onSubmit={save} noValidate>
            <Card className="border-0 shadow-sm mb-3">
                <Card.Body>
                    <h2 className="h5">{programLabel} program page</h2>
                    <p className="text-body-secondary">
                        The web page where students can learn about the{' '}
                        {programLabel} role. The Learning Assistant application
                        links to it when it asks whether a student would also be
                        interested in {programLabel}.
                    </p>
                    <Form.Group controlId={inputId} className="mb-3">
                        <Form.Label>Page address</Form.Label>
                        <Form.Control
                            type="url"
                            value={url}
                            placeholder="https://..."
                            onChange={(event) => setUrl(event.target.value)}
                        />
                    </Form.Group>
                    {saveError && (
                        <Alert variant="danger" role="alert">
                            {saveError}
                        </Alert>
                    )}
                    <div role="status">{notice}</div>
                    <Button
                        type="submit"
                        variant="primary"
                        disabled={saving || unchanged}
                    >
                        {saving ? 'Saving...' : 'Save'}
                    </Button>
                </Card.Body>
            </Card>
        </Form>
    );
}

export default ProgramPageLinkCard;
